import { motion } from "framer-motion";
import { User, Cat, Calendar, AlertTriangle, Rocket, RotateCcw } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { productBadge } from "@/data/productOptions";

export const ModuleCard = ({ module, planet, chapterStats, index = 0, onOpen, onRestore }) => {
  const accent = planet?.color || "#0EA5A4";
  const total = chapterStats?.total || Number(module.planned_chapters) || 0;
  const completed = chapterStats?.completed || 0;
  const pipCount = Math.min(total, 10);
  const completion = Math.max(0, Math.min(100, Number(module.completion) || 0));

  return (
    <motion.div
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      aria-label={`View module ${module.name}`}
      data-testid={`module-card-${module.id}`}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-[#E4E9F3] bg-white p-5 pl-6 text-left shadow-[0_2px_12px_rgba(20,36,95,0.05)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_10px_28px_rgba(20,36,95,0.12)] ${onRestore ? "opacity-70" : ""}`}
    >
      {/* world colour rail */}
      <span className="absolute inset-y-0 left-0 w-2" style={{ backgroundColor: accent }} />

      <div className="flex items-start justify-between gap-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
          style={{ color: accent, backgroundColor: `${accent}14` }}
        >
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />
          {planet?.name || "Unassigned"}
        </span>
        <StatusBadge status={productBadge(module.status)} label={module.status} />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
          style={{ backgroundColor: accent }}
        >
          <Rocket size={20} strokeWidth={2.2} />
        </span>
        <h3 className="font-heading text-base font-semibold leading-snug text-[#14245F]">
          {module.name}
        </h3>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-[#5A6B8C]">
        {module.main_character && <span className="inline-flex items-center gap-1"><Cat size={13} strokeWidth={2} /> {module.main_character}</span>}
        <span className="inline-flex items-center gap-1"><User size={13} strokeWidth={2} /> {module.owner}</span>
        {module.target_date && <span className="inline-flex items-center gap-1"><Calendar size={13} strokeWidth={2} /> {module.target_date}</span>}
      </div>

      {/* chapter progression pips */}
      {pipCount > 0 && (
        <div className="mt-4 flex items-center gap-1.5">
          {Array.from({ length: pipCount }).map((_, i) => (
            <span
              key={`pip-${i}`}
              className="h-1.5 flex-1 rounded-full"
              style={{ backgroundColor: i < completed ? accent : "#E4E9F3" }}
            />
          ))}
          <span className="ml-1 shrink-0 text-[11px] font-bold text-[#5A6B8C]">{completed}/{total}</span>
        </div>
      )}

      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-xs font-medium text-[#5A6B8C]">
          <span>Completion</span>
          <span className="font-bold" style={{ color: accent }}>{completion}%</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#EEF2FB]">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${completion}%`, backgroundColor: accent }} />
        </div>
      </div>

      {module.current_blocker && (
        <div className="mt-3 flex items-start gap-1.5 rounded-xl bg-[#FB6F5A]/[0.08] px-2.5 py-1.5 text-xs font-medium text-[#C2410C]">
          <AlertTriangle size={13} strokeWidth={2} className="mt-0.5 shrink-0" />
          <span className="line-clamp-2">{module.current_blocker}</span>
        </div>
      )}

      {onRestore && (
        <button
          type="button"
          data-testid={`restore-module-${module.id}`}
          onClick={(e) => { e.stopPropagation(); onRestore(); }}
          className="mt-4 inline-flex items-center gap-1 self-start rounded-full border border-[#0EA5A4]/40 px-3 py-1 text-xs font-semibold text-[#0EA5A4] transition-colors hover:bg-[#0EA5A4]/10"
        >
          <RotateCcw size={13} /> Restore
        </button>
      )}
    </motion.div>
  );
};
